import React from 'react';
import { getAbsoluteCoverUrl, getPlaceholderCover } from '../../utils/imageUtils';
import { renderAuthor } from '../../utils/authorDisplay';

/**
 * StoryCard — homepage horizontal-scroll carousel card.
 *
 * Premium-sized for cinematic browsing (Feb 25, 2026):
 *   - Cover ratio stays 2:3 via Tailwind `aspect-[2/3]`. `object-cover`
 *     keeps full quality at any size — no pixelation.
 *   - Card width now ≈ 112 px on mobile (was 80 px), 144 px on sm,
 *     160 px on md+. On a 390 px viewport that's ~2.9 cards visible
 *     with the gutter — the "premium reading app" sweet spot.
 *   - Title scales 10px → 12px proportionally, author 9px → 10px.
 *   - Badges scale to match — no oversized pills. Padding tightened.
 *
 * Feb 25, 2026 — `hideMeta` opt-in:
 *   The homepage carousels (Trending Now, Fresh Reads, etc.) hide the
 *   title + author block under each cover for a cleaner streaming-style
 *   feel. The shared LibraryPage / DiscoverPage grids keep them on
 *   because users browsing those pages are actively searching by name.
 *   - Cover, badges, and click behaviour are untouched in either mode.
 *   - Title is moved to the wrapper's `title` + `aria-label` so it's
 *     still discoverable on hover and announced by screen readers.
 *
 * No section headers, "View All" links, dark theme or navigation are
 * touched here — only the card itself.
 */
const StoryCard = ({ story, onClick, hideMeta = false }) => {
  const authorLabel = renderAuthor(story);
  const accessibleLabel = authorLabel ? `${story.title} by ${authorLabel}` : story.title;
  return (
    <div
      onClick={onClick}
      className="flex-shrink-0 w-[112px] sm:w-36 md:w-40 cursor-pointer group"
      data-testid={`story-card-${story.id}`}
      title={hideMeta ? accessibleLabel : undefined}
      aria-label={hideMeta ? accessibleLabel : undefined}
    >
      <div className="relative overflow-hidden rounded-lg mb-1.5 sm:mb-2 transition-transform group-hover:scale-105 aspect-[2/3] bg-dark-card">
        <img
          src={getAbsoluteCoverUrl(story.cover)}
          alt={story.title}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.target.src = getPlaceholderCover();
          }}
        />
        {/* Contest Badge — only for published opt-in stories */}
        {story.contest_entry && (story.status === 'PUBLISHED' || !story.status) && (
          <span
            className="absolute top-1 left-1 px-1 py-0 rounded bg-yellow-500/90 text-black text-[7px] sm:text-[9px] font-bold leading-tight"
            data-testid={`story-card-contest-${story.id}`}
            title="Entered in contest"
          >
            🏆
          </span>
        )}
        {/* Story Status Badge */}
        <span
          className={`absolute top-1 right-1 px-1 py-0 rounded text-[7px] sm:text-[9px] font-semibold leading-tight ${
            story.is_completed
              ? 'bg-blue-500/90 text-white'
              : 'bg-orange-500/90 text-white'
          }`}
          data-testid={`story-card-status-${story.id}`}
        >
          {story.is_completed ? 'Done' : 'Ongoing'}
        </span>
        {/* Audio Available Badge */}
        <span
          className="absolute bottom-1 left-1 px-1 py-0 rounded bg-purple-500/80 text-white text-[7px] sm:text-[9px] font-medium flex items-center gap-0.5 backdrop-blur-sm leading-tight"
          data-testid={`story-card-audio-${story.id}`}
        >
          <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
          </svg>
          Audio
        </span>
      </div>
      {!hideMeta && (
        <>
          <h3
            className="text-dark-text font-bold text-[10px] sm:text-xs mb-0.5 line-clamp-2 leading-tight"
            data-testid="story-title"
          >
            {story.title}
          </h3>
          {authorLabel && (
            <p
              className="text-dark-text-secondary text-[9px] sm:text-[10px] leading-tight truncate"
              data-testid={`story-card-author-${story.id}`}
            >
              by {authorLabel}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default StoryCard;
